/**
 * Hook: DELETE /api/t/{slug}/candidates/{id} — GDPR-aware candidate erase.
 *
 * Backend cascades the resume file + related rows; see
 * ``backend/app/api/candidates.py:279-290``. The endpoint owns its own
 * ``await db.commit()`` (project_middleware_commit_gap.md workaround), so no
 * ``@touches_candidate`` decorator is needed and there is no row left for
 * the retention-clock to reset (Architect §3.4 / CTO-OQ-2).
 *
 * On success: invalidates the candidate list, the rankings query, AND the
 * dashboard `jd-candidates` card list — the deleted candidate may have been
 * in the latest rankings response and is rendered on the dashboard candidate
 * cards (the `["jd-candidates", …]` query), which must drop them on the next
 * read. The mutation takes the candidate id at call-time (matches the shape
 * of ``useDeleteJd``), not at hook-creation, so the caller doesn't need to
 * re-instantiate the hook for each id.
 *
 * `refetchType: "all"` on every list invalidation is load-bearing
 * (fix/stale-candidate-card-after-delete). The Delete button lives on
 * `<CandidatePage>`; the dashboard `["jd-candidates", …]` / `["candidates",
 * …]` / `["rankings", …]` queries belong to `<Dashboard>`, which is
 * UNMOUNTED while the candidate page is open. With the default
 * `refetchType: "active"`, `invalidateQueries` marks those INACTIVE queries
 * stale but never refetches them — leaving the deleted candidate's stale
 * row sitting in the cache. `refetchType: "all"` forces those inactive
 * queries to refetch.
 *
 * `refetchType: "all"` only *starts* the refetch. The post-delete navigate
 * (`CandidatePage.handleDeleted`, fired from the `.mutate()` call-level
 * `onSuccess` in `CandidatePageHeader`) would remount `<Dashboard>` against
 * still-stale cache if it ran before the refetches resolved. This `onSuccess`
 * therefore RETURNS the `Promise.all` of the invalidations: per React
 * Query v5's mutation lifecycle, when the `useMutation`-level `onSuccess`
 * returns a promise the client awaits it before invoking the call-level
 * `onSuccess` — so the dashboard remounts against a correct cache.
 *
 * Bug D (2026-05-21): this `onSuccess` deliberately does NOT evict the
 * deleted candidate's `["candidate", slug, id]` detail-row query. Doing so
 * mid-mutation unmounts `<CandidatePageHeader>` (the `useMutation` owner)
 * before the awaited `Promise.all` resolves, and React Query v5 then drops
 * the call-level `onSuccess` (the navigate) because the mutation observer
 * has no listeners — see the `onSuccess` body for the full mechanism.
 *
 * 30j-1 — wired from ``<CandidatePage>`` Delete button + confirm modal.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";

export function useDeleteCandidate(): UseMutationResult<void, ApiError, string> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const qc = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: async (candidateId) => {
      await apiClient.delete(
        `/api/t/${tenantSlug}/candidates/${candidateId}`,
      );
    },
    onSuccess: () => {
      // Bug D (2026-05-21): do NOT synchronously `removeQueries` the
      // deleted candidate's `["candidate", slug, id]` detail-row query
      // here.
      //
      // The Delete button + `useDeleteCandidate` live inside
      // `<CandidatePageHeader>`. Evicting the detail-row query mid-mutation
      // makes `<CandidatePage>` flip to its loading/404 branch, which does
      // NOT render `<CandidatePageHeader>` — so the header (and the
      // `useMutation` it owns) UNMOUNTS before this `onSuccess`'s awaited
      // `Promise.all` resolves. React Query v5 gates the `.mutate()`
      // call-level callbacks on `MutationObserver.hasListeners()`
      // (query-core `mutationObserver.js`): once the observer has no
      // subscribed component, the call-level `onSuccess` is silently
      // skipped. That call-level `onSuccess` is the post-delete navigate
      // (`CandidatePageHeader.handleDeleteConfirm` → `onDeleted()`), so the
      // app never leaves the candidate page and the dashboard's refreshed
      // card list is never shown — the founder-reported phantom cards. The
      // first delete races (sometimes the invalidations win), the 2nd/3rd
      // lose the race — exactly the reported asymmetry.
      //
      // The detail-row query is harmless to leave: once the page navigates
      // away it has no observers and is garbage-collected after `gcTime`.
      // The list invalidations below already drop the candidate from every
      // surface that renders it.
      //
      // The three list-invalidation promises are returned so this
      // `onSuccess` resolves only after every `refetchType: "all"` refetch
      // COMPLETES — React Query v5 awaits a promise returned by the
      // mutation-level `onSuccess` before invoking the call-level
      // `onSuccess`, so the dashboard remounts against a correct cache.
      return Promise.all([
        // List invalidation — the deleted row drops out of `useCandidates`.
        qc.invalidateQueries({
          queryKey: ["candidates", tenantSlug],
          refetchType: "all",
        }),
        // Rankings cache: the candidate may have been in the latest run.
        // Invalidate the whole rankings prefix for this tenant — cheap, and
        // matches the pattern in `useStartRankingRun`.
        qc.invalidateQueries({
          queryKey: ["rankings", tenantSlug],
          refetchType: "all",
        }),
        // Dashboard candidate cards: the deleted candidate may be paired to
        // any JD. Invalidate the whole `jd-candidates` prefix for this
        // tenant so its card drops from every JD's dashboard.
        // `refetchType: "all"` is mandatory here — the dashboard is
        // unmounted while the delete fires from `<CandidatePage>`, so its
        // `["jd-candidates", …]` query is INACTIVE; the default `"active"`
        // would leave it stale (the founder-reported lingering card).
        qc.invalidateQueries({
          queryKey: ["jd-candidates", tenantSlug],
          refetchType: "all",
        }),
      ]);
    },
  });
}
